import { parseJsonOrThrow } from "../utils/apiRequestError";
import type {
  CreateNoteBody,
  ListNotesQuery,
  ListNotesResponse,
  Note,
  PatchNoteBody,
  ReportNotesGroupedResponse,
} from "../types/notes";
import { authenticatedFetch } from "./apiClient";
import { mapApiRequestError } from "./apiParseHelpers";

export class NotesApiError extends Error {
  status: number;
  detail?: string;
  slug?: string;

  constructor(message: string, status: number, detail?: string, slug?: string) {
    super(message);
    this.name = "NotesApiError";
    this.status = status;
    this.detail = detail;
    this.slug = slug;
  }
}

async function parseOrThrow(response: Response, fallbackMessage: string): Promise<any> {
  try {
    return await parseJsonOrThrow(response, fallbackMessage, { allow204: true });
  } catch (err) {
    mapApiRequestError(err, (message, status, parsed) =>
      new NotesApiError(message, status, message, parsed.slug)
    );
  }
}

function sortNotesNewestFirst(notes: Note[]): Note[] {
  return [...notes].sort((a, b) => String(b.created_at ?? "").localeCompare(String(a.created_at ?? "")));
}

export async function createNote(body: CreateNoteBody): Promise<Note> {
  const response = await authenticatedFetch("/notes", { method: "POST", body: JSON.stringify(body) });
  return await parseOrThrow(response, `Failed to create note: ${response.status}`);
}

export async function listNotes(query: ListNotesQuery): Promise<ListNotesResponse> {
  const params = new URLSearchParams();
  params.append("entity_type", query.entity_type);
  params.append("entity_id", query.entity_id);
  if (query.scope) params.append("scope", query.scope);
  if (query.tab_key) params.append("tab_key", query.tab_key);
  if (query.limit != null) params.append("limit", String(query.limit));
  if (query.offset != null) params.append("offset", String(query.offset));
  const response = await authenticatedFetch(`/notes?${params.toString()}`);
  const data = await parseOrThrow(response, `Failed to fetch notes: ${response.status}`);
  const notes = sortNotesNewestFirst(Array.isArray(data?.notes) ? data.notes : []);
  return { ...data, notes };
}

export async function listReportNotesGrouped(
  reportId: string,
  perTabLimit = 200
): Promise<ReportNotesGroupedResponse> {
  const params = new URLSearchParams({
    entity_id: reportId,
    per_tab_limit: String(perTabLimit),
  });
  const response = await authenticatedFetch(`/notes/grouped?${params.toString()}`);
  const data = (await parseOrThrow(response, `Failed to fetch grouped notes: ${response.status}`)) as ReportNotesGroupedResponse;
  const master = sortNotesNewestFirst(Array.isArray(data.master) ? data.master : []);
  const tabs: Record<string, Note[]> = {};
  if (data.tabs && typeof data.tabs === "object") {
    for (const [key, list] of Object.entries(data.tabs)) {
      tabs[key] = sortNotesNewestFirst(Array.isArray(list) ? list : []);
    }
  }
  return { ...data, master, tabs };
}

export async function patchNote(noteId: string, body: PatchNoteBody): Promise<Note> {
  const response = await authenticatedFetch(`/notes/${encodeURIComponent(noteId)}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
  return await parseOrThrow(response, `Failed to update note: ${response.status}`);
}

export async function deleteNote(noteId: string): Promise<void> {
  const response = await authenticatedFetch(`/notes/${encodeURIComponent(noteId)}`, { method: "DELETE" });
  await parseOrThrow(response, `Failed to delete note: ${response.status}`);
}
